<template>
  <Card :class="$style.container">
    <div :class="$style.header">
      <p>
        {{ headerString }}
      </p>
      <a :href="route">all stored reports</a>
    </div>
    <table
      v-if="tableData.length > 0"
      :class="$style.table"
    >
      <TableHeader :columns="tableColumns" />
      <TableBodyWithCustomComponents
        :columns="tableColumns"
        :inContainer="true"
        :isRefreshing="isLoading"
        :itemType="itemType"
        :tableData="tableData"
      />
    </table>
  </Card>
</template>

<script>
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import {
  ReportDataFields, ReportListTableColumnConsts, ReportSortFields, ReportStatusConsts,
} from 'Reports/constants/reportsConstants';
import { formatReportsList } from 'Reports/reportsCommonUtils';
import { mapGetters } from 'vuex';
import { notify } from 'client/util';
import Card from 'client/components/Card.vue';
import DownloadReportLink from 'Reports/components/DownloadReportLink.vue';
import ReportProgress from 'Reports/components/ReportProgress.vue';
import ReportsApi from 'client/api/v2/ReportsApi';
import TableBodyWithCustomComponents from 'components/Tables/TableBodyWithCustomComponents.vue';
import TableHeader from 'components/Tables/TableHeader.vue';
import pluralize from 'pluralize';

const { reports } = ItemTypeConsts;
const reportsCollection = StoreModuleMap[reports];

export default {
  name: 'ReportDownloadsPreview',

  components: {
    Card,
    TableBodyWithCustomComponents,
    TableHeader,
  },

  data: () => ({
    downloadsCount: 0,
    fetchInterval: null,
    isLoading: false,
    itemType: reports,
    route: '/#/reports/downloads',
    tableColumns: [
      {
        name: ReportListTableColumnConsts.type,
        dataField: ReportDataFields.type,
      },
      // TODO: To be added back in behind feature flag in RR-215
      // {
      //   name: ReportListTableColumnConsts.parameters,
      //   dataField: ReportDataFields.parameters,
      // },
      {
        name: ReportListTableColumnConsts.requester_email,
        dataField: ReportDataFields.requester_email,
      },
      {
        name: ReportListTableColumnConsts.created_at,
        dataField: ReportDataFields.created_at,
      },
      {
        bodyComponent: ReportProgress,
        bodyComponentProps: {
          item: ReportDataFields.status,
        },
        name: ReportListTableColumnConsts.status,
      },
      {
        bodyComponent: DownloadReportLink,
        bodyComponentProps: {
          item: {
            id: ReportDataFields.id,
            artifacts: ReportDataFields.artifacts,
            status: ReportDataFields.status,
          },
        },
        name: ReportListTableColumnConsts.actions,
      },
    ],
    tableData: [],
  }),

  computed: {
    ...mapGetters(reportsCollection, ['isReportMetadataPending']),

    headerString() {
      if (this.downloadsCount > 0) {
        return `${this.tableData.length} of ${pluralize('report', this.downloadsCount, true)} available.`;
      }
      return 'You have no downloads in your queue.';
    },

    // check if any single report is in pending state
    isReportPending() {
      return this.tableData.some(report => report.status === ReportStatusConsts.pending
        || report.status === ReportStatusConsts.inProgress);
    },
  },

  watch: {
    isReportMetadataPending: {
      handler() {
        this.getTableDataFromStore();
      },
    },
  },

  created() {
    this.getTableDataFromStore();
  },

  beforeDestroy() {
    clearInterval(this.fetchInterval);
    this.fetchInterval = null;
  },

  methods: {
    async getTableDataFromStore() {
      this.isLoading = true;
      try {
        const res = await ReportsApi.fetchReportsList({
          sort: ReportSortFields.created_at,
          skip: 0,
          limit: 3,
        });

        this.downloadsCount = res.data.length;
        this.tableData = this.downloadsCount > 3
          ? formatReportsList(res.data).slice(0, 3) : formatReportsList(res.data);

        // if any single report is pending, set reports list polling interval
        if (this.isReportPending) {
          try {
            this.setPollingInterval();
          } catch (e) {
            notify(
              'error',
              'Unable to fetch reports. Please refresh the page and try again',
            );
          }
        } else {
          // Stop polling if all reports are complete/failed
          clearInterval(this.fetchInterval);
          this.fetchInterval = null;
        }
      } catch (e) {
        notify(
          'error',
          'Unable to fetch reports',
        );
      } finally {
        this.isLoading = false;
      }
    },

    setPollingInterval() {
      if (!this.fetchInterval) {
        this.fetchInterval = setInterval(() => {
          this.getTableDataFromStore();
        }, 1 * 1000);
      }
    },
  },
};
</script>

<style module>
.container {
  display: flex;
  flex-direction: column;
  margin: 10px 0 20px;
}

.header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

a {
  font-weight: 600;
}

.header p {
  font-style: italic;
  margin: 0;
}

.table {
  margin-top: 16px;
  width: 100%;
}
</style>
